// SPDX-License-Identifier: AGPL-3.0-or-later

import type {ChannelMessages} from '@app/features/messaging/state/ChannelMessages';
import type {ScrollerHandle} from '@app/features/ui/components/Scroller';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {type JumpType, JumpTypes} from '@fluxer/constants/src/JumpConstants';
import {compare, extractTimestamp} from '@fluxer/snowflake/src/SnowflakeUtils';

export type ScrollerRef = React.RefObject<ScrollerHandle | null> | React.RefObject<ScrollerHandle>;
export type DebouncedFunction<T> = T extends (...args: infer P) => infer R
	? {
			(...args: P): R;
			cancel(): void;
			flush(): void;
		}
	: never;

export interface AnchorData {
	id: string;
	offsetFromTop: number;
	offsetTop: number;
	offsetHeight: number;
	clamped: boolean;
}

export interface ScrollerState {
	scrollTop: number;
	scrollHeight: number;
	offsetHeight: number;
}

export const DEFAULT_SCROLLER_STATE: ScrollerState = {
	scrollTop: 0,
	scrollHeight: 0,
	offsetHeight: 0,
};
export const BOTTOM_LOCK_TOLERANCE = 8;
export const CENTRE_ALIGNMENT_LIFT = 8;
export const MESSAGE_REVEAL_PADDING = 16;
export const UNREAD_LOAD_TRIGGER_LIFT = 16;
const RESIZE_STICK_MIN_THRESHOLD = 64;

export type ContainerResizeShift = {kind: 'none'} | {kind: 'pin'} | {kind: 'shift'; targetScrollTop: number};

export function resolveContainerResizeShift(options: {
	heightDelta: number;
	isPinned: boolean;
	editIsActive: boolean;
	state: ScrollerState;
}): ContainerResizeShift {
	const {heightDelta, isPinned, editIsActive, state} = options;
	if (heightDelta === 0) {
		return {kind: 'none'};
	}
	if (isPinned) {
		return {kind: 'pin'};
	}
	if (editIsActive) {
		return {kind: 'none'};
	}
	const distanceFromBottom = Math.max(state.scrollHeight - state.offsetHeight - state.scrollTop, 0);
	const stickThreshold = Math.max(Math.abs(heightDelta) + BOTTOM_LOCK_TOLERANCE, RESIZE_STICK_MIN_THRESHOLD);
	if (distanceFromBottom > stickThreshold) {
		return {kind: 'none'};
	}
	const maxScrollTop = Math.max(0, state.scrollHeight - state.offsetHeight);
	const targetScrollTop = Math.max(0, Math.min(state.scrollTop + heightDelta, maxScrollTop));
	return {kind: 'shift', targetScrollTop};
}

export const InitialScrollIntent = Object.freeze({
	SAVED_OFFSET: 'savedOffset',
	UNREAD_BOUNDARY: 'unreadBoundary',
	BOTTOM: 'bottom',
} as const);

export type InitialScrollIntent = (typeof InitialScrollIntent)[keyof typeof InitialScrollIntent];

const UNREAD_ANCHOR_EXEMPT_CHANNEL_TYPES: ReadonlySet<number> = new Set<number>([ChannelTypes.GUILD_VOICE]);

export interface InitialScrollIntentInput {
	readonly channelType: number | null | undefined;
	readonly rememberedScrollTop: number | null;
	readonly hasPendingUnreads: boolean;
}

export function resolveInitialScrollIntent(input: InitialScrollIntentInput): InitialScrollIntent {
	if (input.hasPendingUnreads && !UNREAD_ANCHOR_EXEMPT_CHANNEL_TYPES.has(input.channelType ?? -1)) {
		return InitialScrollIntent.UNREAD_BOUNDARY;
	}
	return input.rememberedScrollTop != null ? InitialScrollIntent.SAVED_OFFSET : InitialScrollIntent.BOTTOM;
}

export const UnreadBoundaryFallback = Object.freeze({
	LAST_READ: 'lastRead',
	JUMP_LAST_READ: 'jumpLastRead',
	BOTTOM: 'bottom',
	NONE: 'none',
} as const);

export type UnreadBoundaryFallback = (typeof UnreadBoundaryFallback)[keyof typeof UnreadBoundaryFallback];

export function resolveUnreadBoundaryFallback(input: {
	lastReadElementPresent: boolean;
	lastReadMessageId: string | null;
	jumpDestinationId?: string | null;
}): UnreadBoundaryFallback {
	if (input.jumpDestinationId != null && input.jumpDestinationId !== input.lastReadMessageId) {
		return UnreadBoundaryFallback.NONE;
	}
	if (input.lastReadElementPresent) {
		return UnreadBoundaryFallback.LAST_READ;
	}
	if (input.lastReadMessageId != null) {
		return UnreadBoundaryFallback.JUMP_LAST_READ;
	}
	return UnreadBoundaryFallback.BOTTOM;
}

/**
 * True when a "wait for the jump destination to load" hold should be dropped.
 *
 * Both wait paths deliberately leave the jump active with no scroll queued, so a page that
 * arrives later can still land the jump. Nothing releases that hold if the page never arrives,
 * and an active jump keeps `loadIsDisabled()` true, which stops history paging for the rest of
 * the mount. Once the destination is no longer the one being waited on there is nothing to land.
 */
export function shouldReleaseJumpHold(input: {
	pendingDestinationId: string | null;
	currentDestinationId: string | null;
}): boolean {
	return input.pendingDestinationId != null && input.currentDestinationId !== input.pendingDestinationId;
}

export type HistoryLoadPauseAction = 'clear' | 'pause' | 'keep';

/**
 * History paging must not chain off layout. After a page lands — including a
 * cached drain that never toggles `loadingMore` — stay paused until the user
 * scrolls again. Restore can leave the viewport in the top trigger; fetching
 * from there eats several pages and jumps far up-history.
 */
export function resolveHistoryLoadPauseAfterSettle(input: {
	channelChanged: boolean;
	loadingMoreWentIdle: boolean;
}): HistoryLoadPauseAction {
	if (input.channelChanged) return 'clear';
	if (input.loadingMoreWentIdle) return 'pause';
	return 'keep';
}

export function shouldResumeHistoryLoadOnUserScroll(input: {
	paused: boolean;
	isUserGesture: boolean;
	loadInProgress: boolean;
}): boolean {
	return input.paused && input.isUserGesture && !input.loadInProgress;
}

/** Always load from the message list, never from inside the head filler. */
export function resolveHistoryLoadTopTriggerOffset(input: {hasMoreBefore: boolean; spacerHeight: number}): number {
	if (!input.hasMoreBefore) return 0;
	return input.spacerHeight + 500;
}

/**
 * Keep the same messages on screen after a prepend. Lift out of the head filler
 * and just below the load trigger so a continued flick walks through the new
 * page instead of chaining another fetch.
 */
export function resolveHistoryPrependRestoreScrollTop(input: {
	snapshotScrollTop: number;
	addedHeight: number;
	maxScroll: number;
	spacerHeight: number;
	topTriggerOffset: number;
}): number {
	const restored = Math.max(0, Math.min(input.snapshotScrollTop + input.addedHeight, input.maxScroll));
	const minVisible = Math.min(Math.max(input.spacerHeight, 0), input.maxScroll);
	const minOutsideTrigger = Math.min(Math.max(input.topTriggerOffset + 2, 0), input.maxScroll);
	return Math.max(restored, minVisible, minOutsideTrigger);
}

/**
 * Cached pages settle in the same turn as the fetch, so "in flight" is not a
 * useful window. Whenever older history exists, do not let the viewport sit in
 * the head filler.
 */
export function resolveHistoryLoadMinScrollTop(input: {
	hasMoreBefore: boolean;
	spacerHeight: number;
	maxScroll: number;
}): number | null {
	if (!input.hasMoreBefore || input.spacerHeight <= 0) return null;
	return Math.min(input.spacerHeight, Math.max(0, input.maxScroll));
}

/**
 * Overscroll through the head filler used to park at `spacer - viewport`, which
 * is a full screen of skeleton. Park at the filler/message boundary instead.
 */
export function resolveHistoryLoadFillerOverscrollTop(input: {
	inTopPlaceholder: boolean;
	nextScrollTop: number;
	spacerHeight: number;
}): number | null {
	if (!input.inTopPlaceholder || input.nextScrollTop > 0) return null;
	return input.spacerHeight;
}

export function resolveUnreadLandAlignment(input: {
	jumpDestinationId: string | null;
	jumpHighlight: boolean;
	lastReadMessageId: string | null;
}): 'start' | 'center' {
	if (input.jumpHighlight) {
		return 'center';
	}
	if (input.jumpDestinationId != null && input.jumpDestinationId === input.lastReadMessageId) {
		return 'start';
	}
	return 'center';
}

export enum ScrollRegion {
	None = 0,
	Top = 1,
	Bottom = 2,
}

/** Load the next page when the viewport crosses into a trigger, not while it sits there. */
export function shouldFetchHistoryPageOnTriggerEnter(input: {
	currentRegion: ScrollRegion;
	previousRegion: ScrollRegion;
}): boolean {
	if (input.currentRegion === ScrollRegion.None) return false;
	return input.currentRegion !== input.previousRegion;
}

export function shouldAnimateMessageJump(jumpType: JumpType): boolean {
	return jumpType === JumpTypes.ANIMATED;
}

export const JumpPreSnap = Object.freeze({
	NONE: 'none',
	START: 'start',
	END: 'end',
} as const);

export type JumpPreSnap = (typeof JumpPreSnap)[keyof typeof JumpPreSnap];

export interface JumpPreSnapInput {
	readonly targetId: string;
	readonly fromTimestamp: number | null;
	readonly animate: boolean;
	readonly alreadyJumping: boolean;
	readonly reducedMotion: boolean;
}

export function resolveJumpPreSnap(input: JumpPreSnapInput): JumpPreSnap {
	if (input.alreadyJumping) return JumpPreSnap.NONE;
	if (!input.animate) return JumpPreSnap.NONE;
	if (input.fromTimestamp == null) return JumpPreSnap.NONE;
	if (input.reducedMotion) return JumpPreSnap.NONE;
	return extractTimestamp(input.targetId) > input.fromTimestamp ? JumpPreSnap.START : JumpPreSnap.END;
}

export function resolveJumpPreSnapOrigin(previous: ChannelMessages, next: ChannelMessages): number | null {
	const previousOldest = previous.first();
	if (previousOldest == null) return null;
	if (next.last()?.id === previous.last()?.id) return null;
	if (next.first()?.id === previous.first()?.id) return null;
	return extractTimestamp(previousOldest.id);
}

export function resolveJumpTargetId(messages: ChannelMessages): string | null {
	const {jumpDestinationId, jumpDestinationOffset} = messages;
	if (!jumpDestinationId || !messages.ready) return null;
	if (messages.has(jumpDestinationId) || (!messages.hasMoreBefore && jumpDestinationId === messages.channelId)) {
		if (jumpDestinationOffset === 0) {
			return jumpDestinationId;
		}
		const index = messages.indexOf(jumpDestinationId);
		const targetMessage = messages.atIndex(index + jumpDestinationOffset);
		return targetMessage?.id ?? jumpDestinationId;
	}
	const allIds = [jumpDestinationId, ...messages.map((m) => m.id)].sort(compare);
	const jumpIndex = allIds.indexOf(jumpDestinationId);
	const offset = Math.abs(jumpDestinationOffset) > 0 ? jumpDestinationOffset : 1;
	const closestId = allIds[jumpIndex + offset] ?? allIds[jumpIndex - 1];
	return closestId ?? null;
}
